export interface SuspendableResource<T> {
  read(): T;
}

/**
 * Wraps a Promise into a resource that can be `read()` from inside a React
 * component rendered under a `<Suspense>` boundary.
 *
 * While the promise is pending, `read()` throws the promise (suspending the
 * component). On rejection, `read()` throws the error (caught by the nearest
 * error boundary). On fulfilment, `read()` synchronously returns the value.
 *
 * The returned resource is single-shot and stateless w.r.t. retries: it
 * neither caches across calls nor re-runs the underlying promise. Callers
 * that need a "fetch-once" semantic must hold the resource in a stable
 * location (e.g. module scope, `WeakMap`) — note that component-local state
 * is lost when `<Suspense>` unmounts the suspending subtree, so `useRef` /
 * `useMemo` are not sufficient.
 */
export const createSuspendableResource = <T,>(
  promise: Promise<T>,
): SuspendableResource<T> => {
  let status: "pending" | "success" | "error" = "pending";
  let result: T;
  let error: unknown;
  const suspender = promise.then(
    (value) => {
      status = "success";
      result = value;
    },
    (e) => {
      status = "error";
      error = e;
    },
  );
  return {
    read(): T {
      if (status === "pending") {
        throw suspender;
      }
      if (status === "error") {
        throw error;
      }
      return result;
    },
  };
};
